import { useEffect, useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { analyzeEstimate, type EstimateAnalysisResults } from "@/utils/estimateAnalysis";
import { Button } from "@/components/ui/button";
import { FileSearch } from "lucide-react";
import { EstimateAnalysis } from "@/components/analysis/EstimateAnalysis";
import type { Json } from "@/integrations/supabase/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface RepairEstimateDetailsProps {
  vinLookupId?: string;
}

interface ProcessedData {
  preprocessed_text?: string;
  repairLines?: {
    headers?: string[];
    rows?: Array<{
      operation: string;
      description: string;
      laborType?: string;
      partType?: string;
      quantity?: number;
      price?: number | string;
    }>;
  };
  [key: string]: any; // Allow for other properties
}

interface EstimateData {
  id: string;
  original_filename: string;
  extracted_text: string;
  raw_text?: string;
  processed_at: string;
  vin: string;
  ro_number: string | null;
  processed_data: ProcessedData | null;
}

// Helper function to format JSON data in a readable way
const formatJsonData = (data: ProcessedData | null) => {
  if (!data) return null;
  
  try {
    // Format specific fields we know about
    const formattedData = {
      ...data,
      repairLines: Array.isArray(data.repairLines?.rows) 
        ? {
            ...data.repairLines,
            rows: data.repairLines.rows.map(line => ({
              ...line,
              price: typeof line.price === 'number' ? `$${line.price.toFixed(2)}` : line.price,
            }))
          }
        : data.repairLines,
    };
    
    return formattedData;
  } catch (error) {
    console.error('Error formatting JSON:', error);
    return data;
  }
};

export const RepairEstimateDetails = ({ vinLookupId }: RepairEstimateDetailsProps) => {
  const [loading, setLoading] = useState(true);
  const [estimate, setEstimate] = useState<EstimateData | null>(null);
  const [processingEstimate, setProcessingEstimate] = useState(false);
  const [estimateAnalysis, setEstimateAnalysis] = useState<EstimateAnalysisResults | null>(null);
  const [showStructuredData, setShowStructuredData] = useState(false);
  const [vinData, setVinData] = useState<any>(null);

  useEffect(() => {
    const fetchEstimate = async () => {
      if (!vinLookupId) return;

      try {
        const { data: vinLookup, error: vinError } = await supabase
          .from('vin_lookups')
          .select('vin, raw_dataone_response')
          .eq('id', vinLookupId)
          .maybeSingle();

        if (vinError) {
          console.error('Error fetching VIN lookup:', vinError);
          return;
        }

        if (vinLookup && vinLookup.vin) {
          setVinData(vinLookup);

          const { data: estimateData, error: estimateError } = await supabase
            .from('estimates')
            .select('*')
            .eq('vin', vinLookup.vin)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          if (estimateError) {
            console.error('Error fetching estimate:', estimateError);
            return;
          }

          setEstimate(estimateData as EstimateData);
        } else {
          console.log('No VIN found for lookup ID:', vinLookupId);
        }
      } catch (error) {
        console.error('Error in fetchEstimate:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEstimate();
  }, [vinLookupId]);

  const handleViewStructuredData = async () => {
    if (!estimate) return;
    
    setProcessingEstimate(true);
    try {
      const results = await analyzeEstimate(estimate.id);
      setEstimateAnalysis(results);
      setShowStructuredData(true);
    } catch (error) {
      console.error("Error analyzing estimate:", error);
    } finally {
      setProcessingEstimate(false);
    }
  };

  if (loading) {
    return <Skeleton className="w-full h-[200px]" />;
  }

  if (!estimate) {
    return <div className="text-muted-foreground text-sm">No repair estimate found for this VIN.</div>;
  }

  const adasFeatures = vinData?.raw_dataone_response?.adas_features || null;

  return (
    <div className="space-y-4">
      <div className="mb-6 flex space-x-2">
        <Dialog open={showStructuredData} onOpenChange={setShowStructuredData}>
          <DialogTrigger asChild>
            <Button
              variant="default"
              onClick={handleViewStructuredData}
              disabled={processingEstimate}
              className="w-full sm:w-auto"
            >
              <FileSearch className="mr-2 h-4 w-4" />
              {processingEstimate ? "Processing..." : "View Processed Data"}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Processed Data</DialogTitle>
              <DialogDescription>
                View extracted text, preprocessed text, and structured data
              </DialogDescription>
            </DialogHeader>
            <Tabs defaultValue="structured" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="structured">Structured Data</TabsTrigger>
                <TabsTrigger value="raw">Raw Text</TabsTrigger>
                <TabsTrigger value="comparison">Text Comparison</TabsTrigger>
              </TabsList>
              <TabsContent value="structured" className="mt-4">
                <ScrollArea className="h-[600px]">
                  <div className="space-y-6">
                    {/* Formatted JSON Response */}
                    {estimate.processed_data && (
                      <div className="space-y-2">
                        <h3 className="text-sm font-medium">Formatted Data</h3>
                        <div className="p-4 bg-muted rounded-md">
                          <pre className="text-sm font-mono whitespace-pre-wrap overflow-x-auto">
                            {JSON.stringify(formatJsonData(estimate.processed_data), null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Formatted Structured Data Table */}
                    {estimateAnalysis?.processedEstimate && (
                      <>
                        <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                          <div>
                            <h3 className="font-medium text-sm">VIN</h3>
                            <p className="text-sm text-muted-foreground">
                              {estimateAnalysis.processedEstimate.vehicle?.vin || 'Not detected'}
                            </p>
                          </div>
                          <div>
                            <h3 className="font-medium text-sm">RO Number</h3>
                            <p className="text-sm text-muted-foreground">
                              {estimateAnalysis.processedEstimate.roNumber || 'Not detected'}
                            </p>
                          </div>
                        </div>

                        {estimateAnalysis.processedEstimate.repairLines && (
                          <div>
                            <h3 className="text-lg font-semibold mb-4">Repair Lines</h3>
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  {estimateAnalysis.processedEstimate.repairLines.headers?.map((header, index) => (
                                    <TableHead key={index}>{header}</TableHead>
                                  ))}
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {estimateAnalysis.processedEstimate.repairLines.rows?.map((row, rowIndex) => (
                                  <TableRow key={rowIndex}>
                                    <TableCell>{row.operation}</TableCell>
                                    <TableCell className="max-w-md">{row.description}</TableCell>
                                    <TableCell>{row.laborType}</TableCell>
                                    <TableCell>{row.partType}</TableCell>
                                    <TableCell>{row.quantity}</TableCell>
                                    <TableCell>{typeof row.price === 'number' ? `$${row.price.toFixed(2)}` : row.price}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>
              <TabsContent value="raw" className="mt-4">
                <ScrollArea className="h-[600px]">
                  <pre className="p-4 bg-muted rounded-md text-sm font-mono whitespace-pre-wrap">
                    {estimate.extracted_text}
                  </pre>
                </ScrollArea>
              </TabsContent>
              <TabsContent value="comparison" className="mt-4">
                <ScrollArea className="h-[600px]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <h3 className="text-sm font-medium mb-2">Original Text</h3>
                      <pre className="p-4 bg-muted rounded-md text-sm font-mono whitespace-pre-wrap h-[500px] overflow-y-auto">
                        {estimate.extracted_text}
                      </pre>
                    </div>
                    <div>
                      <h3 className="text-sm font-medium mb-2">Preprocessed Text</h3>
                      <pre className="p-4 bg-muted rounded-md text-sm font-mono whitespace-pre-wrap h-[500px] overflow-y-auto">
                        {estimate.processed_data?.preprocessed_text || 'No preprocessed text available'}
                      </pre>
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {estimate.extracted_text && vinData && (
          <EstimateAnalysis 
            vin={vinData.vin}
            estimateText={estimate.extracted_text}
            adasFeatures={adasFeatures}
          />
        )}
      </div>

      <div>
        <h3 className="text-sm font-medium">Original File</h3>
        <p className="text-sm text-muted-foreground">{estimate.original_filename}</p>
      </div>

      <div>
        <h3 className="text-sm font-medium">Processed Date</h3>
        <p className="text-sm text-muted-foreground">
          {new Date(estimate.processed_at).toLocaleString()}
        </p>
      </div>

      <div>
        <h3 className="text-sm font-medium">VIN</h3>
        <p className="text-sm text-muted-foreground">{estimate.vin}</p>
      </div>

      <div>
        <h3 className="text-sm font-medium">RO Number</h3>
        <p className="text-sm text-muted-foreground">{estimate.ro_number || 'Not detected'}</p>
      </div>
    </div>
  );
};
